import { Globals } from "@react-spring/core";
import { unstable_batchedUpdates } from "react-dom";
import { FluidValue, addFluidObserver, callFluidObservers, colors, createStringInterpolator, each, eachProp, getFluidValue, hasFluidValue, is, removeFluidObserver, toArray } from "@react-spring/shared";
import { AnimatedObject, createHost } from "@react-spring/animated";

export * from "@react-spring/core"

//#region src/applyAnimatedValues.ts
const isCustomPropRE = /^--/;
function dangerousStyleValue(name, value) {
	if (value == null || typeof value === "boolean" || value === "") return "";
	if (typeof value === "number" && value !== 0 && !isCustomPropRE.test(name) && !(isUnitlessNumber.hasOwnProperty(name) && isUnitlessNumber[name])) return value + "px";
	return ("" + value).trim();
}
const attributeCache = {};
function applyAnimatedValues(instance, props) {
	if (!instance.nodeType || !instance.setAttribute || !instance.removeAttribute) return false;
	const isFilterElement = instance.nodeName === "filter" || instance.parentNode && instance.parentNode.nodeName === "filter";
	const { className, style, children, scrollTop, scrollLeft, viewBox, ...attributes } = props;
	const values = Object.values(attributes);
	const names = Object.keys(attributes).map((name) => isFilterElement || instance.hasAttribute(name) ? name : attributeCache[name] || (attributeCache[name] = name.replace(/([A-Z])/g, (n) => "-" + n.toLowerCase())));
	if (props.hasOwnProperty("children")) instance.textContent = children;
	for (const name in style) if (style.hasOwnProperty(name)) {
		const value = dangerousStyleValue(name, style[name]);
		if (isCustomPropRE.test(name)) instance.style.setProperty(name, value);
		else instance.style[name] = value;
	}
	names.forEach((name, i) => {
		const value = values[i];
		if (value !== void 0) instance.setAttribute(name, value);
		else instance.removeAttribute(name);
	});
	if (props.hasOwnProperty("className")) if (className !== void 0) instance.className = className;
	else instance.removeAttribute("class");
	if (scrollTop !== void 0) instance.scrollTop = scrollTop;
	if (scrollLeft !== void 0) instance.scrollLeft = scrollLeft;
	if (props.hasOwnProperty("viewBox")) if (viewBox !== void 0) instance.setAttribute("viewBox", viewBox);
	else instance.removeAttribute("viewBox");
}
let isUnitlessNumber = {
	animationIterationCount: true,
	borderImageOutset: true,
	borderImageSlice: true,
	borderImageWidth: true,
	boxFlex: true,
	boxFlexGroup: true,
	boxOrdinalGroup: true,
	columnCount: true,
	columns: true,
	flex: true,
	flexGrow: true,
	flexPositive: true,
	flexShrink: true,
	flexNegative: true,
	flexOrder: true,
	gridRow: true,
	gridRowEnd: true,
	gridRowSpan: true,
	gridRowStart: true,
	gridColumn: true,
	gridColumnEnd: true,
	gridColumnSpan: true,
	gridColumnStart: true,
	fontWeight: true,
	lineClamp: true,
	lineHeight: true,
	opacity: true,
	order: true,
	orphans: true,
	tabSize: true,
	widows: true,
	zIndex: true,
	zoom: true,
	fillOpacity: true,
	floodOpacity: true,
	stopOpacity: true,
	strokeDasharray: true,
	strokeDashoffset: true,
	strokeMiterlimit: true,
	strokeOpacity: true,
	strokeWidth: true
};
const prefixKey = (prefix, key) => prefix + key.charAt(0).toUpperCase() + key.substring(1);
const prefixes = [
	"Webkit",
	"Ms",
	"Moz",
	"O"
];
isUnitlessNumber = Object.keys(isUnitlessNumber).reduce((acc, prop) => {
	prefixes.forEach((prefix) => acc[prefixKey(prefix, prop)] = acc[prop]);
	return acc;
}, isUnitlessNumber);

//#endregion
//#region src/AnimatedStyle.ts
/** The transform-functions
* (https://developer.mozilla.org/fr/docs/Web/CSS/transform-function)
* that you can pass as keys to your animated component style and that will be
* animated. Perspective has been left out as it would conflict with the
* non-transform perspective style.
*/
const domTransforms = /^(matrix3d|matrix|translate3d|translate[XYZ]?|scale3d|scale[XYZ]?|rotate3d|rotate[XYZ]?|skew[XY]?)$/;
const pxTransforms = /^(translate)/;
const degTransforms = /^(rotate|skew)/;
/** Add a unit to the value when the value is unit-less (eg: a number) */
const addUnit = (value, unit) => is.num(value) && value !== 0 ? value + unit : value;
/**
* Checks if the input value matches the identity value.
*
*     isValueIdentity(0, 0)              // => true
*     isValueIdentity('0px', 0)          // => true
*     isValueIdentity([0, '0px', 0], 0)  // => true
*/
const isValueIdentity = (value, id) => is.arr(value) ? value.every((v) => isValueIdentity(v, id)) : is.num(value) ? value === id : parseFloat(value) === id;
/**
* This AnimatedStyle will simplify animated components transforms by
* interpolating all transform function passed as keys in the style object
* including shortcuts such as x, y and z for translateX/Y/Z
*/
var AnimatedStyle = class extends AnimatedObject {
	constructor({ x, y, z, ...style }) {
		/**
		* An array of arrays that contains the values (static or fluid)
		* used by each transform function.
		*/
		const inputs = [];
		/**
		* An array of functions that take a list of values (static or fluid)
		* and returns (1) a CSS transform string and (2) a boolean that's true
		* when the transform has no effect (eg: an identity transform).
		*/
		const transforms = [];
		if (x || y || z) {
			inputs.push([
				x || 0,
				y || 0,
				z || 0
			]);
			transforms.push((xyz) => [`translate3d(${xyz.map((v) => addUnit(v, "px")).join(",")})`, isValueIdentity(xyz, 0)]);
		}
		eachProp(style, (value, key) => {
			if (key === "transform") {
				inputs.push([value || ""]);
				transforms.push((transform) => [transform, transform === ""]);
			} else if (domTransforms.test(key)) {
				delete style[key];
				if (is.und(value)) return;
				const unit = pxTransforms.test(key) ? "px" : degTransforms.test(key) ? "deg" : "";
				inputs.push(toArray(value));
				transforms.push(key === "rotate3d" ? ([x, y, z, deg]) => [`rotate3d(${x},${y},${z},${addUnit(deg, unit)})`, isValueIdentity(deg, 0)] : (input) => [`${key}(${input.map((v) => addUnit(v, unit)).join(",")})`, isValueIdentity(input, key.startsWith("scale") ? 1 : 0)]);
			}
		});
		if (inputs.length) style.transform = new FluidTransform(inputs, transforms);
		super(style);
	}
};
/** @internal */
var FluidTransform = class extends FluidValue {
	constructor(inputs, transforms) {
		super();
		this.inputs = inputs;
		this.transforms = transforms;
		this._value = null;
	}
	get() {
		return this._value || (this._value = this._get());
	}
	_get() {
		let transform = "";
		let identity = true;
		each(this.inputs, (input, i) => {
			const arg1 = getFluidValue(input[0]);
			const [t, id] = this.transforms[i](is.arr(arg1) ? arg1 : input.map(getFluidValue));
			transform += " " + t;
			identity = identity && id;
		});
		return identity ? "none" : transform;
	}
	observerAdded(count) {
		if (count == 1) each(this.inputs, (input) => each(input, (value) => hasFluidValue(value) && addFluidObserver(value, this)));
	}
	observerRemoved(count) {
		if (count == 0) each(this.inputs, (input) => each(input, (value) => hasFluidValue(value) && removeFluidObserver(value, this)));
	}
	eventObserved(event) {
		if (event.type == "change") this._value = null;
		callFluidObservers(this, event);
	}
};

//#endregion
//#region src/primitives.ts
const primitives = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan"
];

//#endregion
//#region src/index.ts
Globals.assign({
	batchedUpdates: unstable_batchedUpdates,
	createStringInterpolator,
	colors
});
const host = createHost(primitives, {
	applyAnimatedValues,
	createAnimatedStyle: (style) => new AnimatedStyle(style),
	getComponentProps: ({ scrollTop, scrollLeft, ...props }) => props
});
const animated = host.animated;

//#endregion
export { animated as a, animated };